/**
 * SignInModal: sign-in dialog with context copy ("Sign in to share this
 * plan"). Used by useRequireAuth to gate account-only actions for guests.
 * On success, onSuccess fires (the pending action runs).
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { ApiError } from '../lib/api';
import { useAuthStore } from '../stores/auth';

function errorMessage(error: unknown, t: (key: string) => string): string {
  if (error instanceof ApiError) return error.message;
  return error instanceof Error ? error.message : t('auth.signInFailed');
}

export function SignInModal({
  open,
  context,
  onClose,
  onSuccess,
}: {
  open: boolean;
  context: string;
  onClose: () => void;
  onSuccess: () => void;
}): JSX.Element | null {
  const { t } = useTranslation();
  const login = useAuthStore((s) => s.login);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!open) return null;

  async function handleSubmit(event: React.FormEvent): Promise<void> {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await login(email.trim(), password);
      setEmail('');
      setPassword('');
      onSuccess();
    } catch (err) {
      setError(errorMessage(err, t));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={t('auth.signInButton')}
      className="fixed inset-0 z-50 flex items-center justify-center bg-ink/40 p-4 backdrop-blur-[2px]"
      onClick={onClose}
    >
      <div
        className="w-full max-w-sm rounded-2xl bg-white p-7 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="text-xl font-extrabold tracking-tight text-ink">{t('auth.signInToContinue')}</h2>
        {context && <p className="mt-1 text-sm text-stone-600">{context}</p>}
        <form onSubmit={(e) => void handleSubmit(e)} className="mt-4 space-y-3">
          <label className="field-label">
            {t('auth.email')}
            <input
              type="email"
              required
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="field-input"
            />
          </label>
          <label className="field-label">
            {t('auth.password')}
            <input
              type="password"
              required
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="field-input"
            />
          </label>
          {error && (
            <p role="alert" className="text-sm text-red-600">
              {error}
            </p>
          )}
          <button
            type="submit"
            disabled={busy}
            className="btn-primary w-full py-2.5"
          >
            {busy ? t('auth.signingIn') : t('auth.signInButton')}
          </button>
        </form>
        <div className="mt-4 flex items-center justify-between text-sm">
          <Link to="/signup" onClick={onClose} className="font-medium text-tenant-primary hover:underline">
            {t('auth.createFree')}
          </Link>
          <Link to="/forgot-password" onClick={onClose} className="text-stone-500 hover:underline">
            {t('auth.forgotPassword')}
          </Link>
        </div>
      </div>
    </div>
  );
}
